import { DateTime } from "luxon"
import { SalesReportSettingsType } from "../SalesReport"
import { Preferences } from "@capacitor/preferences"

export const dateUtils = {
    isSameDay: (date1: Date, date2: Date) =>
        date1.getUTCFullYear() === date2.getUTCFullYear() &&
        date1.getUTCMonth() === date2.getUTCMonth() &&
        date1.getUTCDate() === date2.getUTCDate(),

    isSameWeek: (date1: Date, date2: Date) => {
        const firstDayWeek1 = date1.getUTCDate() - date1.getUTCDay()
        const firstDayWeek2 = date2.getUTCDate() - date2.getUTCDay()
        return date1.getUTCFullYear() === date2.getUTCFullYear() &&
            date1.getUTCMonth() === date2.getUTCMonth() &&
            firstDayWeek1 === firstDayWeek2
    },

    isSameMonth: (date1: Date, date2: Date) =>
        date1.getUTCFullYear() === date2.getUTCFullYear() &&
        date1.getUTCMonth() === date2.getUTCMonth(),

    isSameYear: (date1: Date, date2: Date) => date1.getUTCFullYear() === date2.getUTCFullYear(),

    monthIsBefore: (date1: Date, date2: Date) =>
        date1.getUTCFullYear() < date2.getUTCFullYear() ||
        (date1.getUTCFullYear() === date2.getUTCFullYear() && date1.getUTCMonth() < date2.getUTCMonth()),

    createUTCDate: (year?: number, month?: number, day?: number, hour?: number, minutes?: number, seconds?: number) => {

        const date = new Date()
        date.setUTCFullYear(year ?? date.getUTCFullYear())
        date.setUTCMonth(month ?? date.getUTCMonth())
        date.setUTCDate(day ?? date.getUTCDate())
        date.setUTCHours(hour ?? date.getUTCHours())
        date.setUTCMinutes(minutes ?? date.getUTCMinutes())
        date.setUTCSeconds(seconds ?? date.getUTCSeconds())
        return date
    },

    getSamesDayWeekBefore: (date: Date) => {
        const newDate = new Date(date)
        newDate.setUTCDate(date.getUTCDate() - 7)
        newDate.setUTCHours(4, 0, 0, 0)
        return newDate
    },

    getFirstDayThisMonth: (date: Date) => {
        const newDate = new Date(date)
        newDate.setUTCDate(1)
        newDate.setUTCHours(4, 0, 0, 0)
        return newDate
    },

    getCurrentDay: () => {
        const today = new Date()
        today.setUTCHours(4, 0, 0, 0)
        return today
    },

    getFirstDayPreviousMonth: (date: Date) => {
        const newDate = new Date(date)
        newDate.setUTCMonth(newDate.getUTCMonth() - 1)
        newDate.setUTCDate(1)
        newDate.setUTCHours(4, 0, 0, 0)
        return newDate
    },

    getFirstDayPreviousYear: (date: Date) => {
        const newDate = new Date(date)
        newDate.setUTCFullYear(newDate.getUTCFullYear() - 1)
        newDate.setUTCMonth(0)
        newDate.setUTCDate(1)
        newDate.setUTCHours(4, 0, 0, 0)
        return newDate
    },

    getFirstDayThisYear: (date: Date) => {
        const newDate = new Date(date)
        newDate.setUTCMonth(0)
        newDate.setUTCDate(1)
        newDate.setUTCHours(4, 0, 0, 0)
        return newDate
    },

    formatDay: (date: Date) => {
        return new Intl.DateTimeFormat(navigator.language, {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
        }).format(date)
    },

    formatWeek: (date: Date) => {
        const endDate = new Date(date)
        endDate.setUTCDate(date.getUTCDate() + 6)
        return `${new Intl.DateTimeFormat(navigator.language, {
            day: 'numeric',
            month: 'long'
        }).format(date)} - ${new Intl.DateTimeFormat(navigator.language, {
            day: 'numeric',
            month: 'long'
        }).format(endDate)}`
    },

    formatMonth: (date: Date) => {
        return new Intl.DateTimeFormat(navigator.language, {
            month: 'long',
            year: 'numeric'
        }).format(date)
    },

    formatYear: (date: Date) => {
        return new Intl.DateTimeFormat(navigator.language, {
            year: 'numeric'
        }).format(date)
    },

    getDefaultPreviousDate(intervalType: SalesReportSettingsType, interval: number, date?: DateTime): DateTime {
        if (!date) {
            date = DateTime.now()

        }
        const intervals = {
            'daily': { days: interval },
            'weekly': { weeks: interval },
            'monthly': { months: interval },
            'yearly': { years: interval },
            'custom': { days: interval },
        };



        const intervalToSubtract = intervals[intervalType] ?? { days: interval };
        return date.minus(intervalToSubtract);
    },

    getFormatForReportType(reportType: SalesReportSettingsType) {
        let format: Intl.DateTimeFormatOptions = {}
        switch (reportType) {
            case 'daily':
                format = { hour: '2-digit' }
                break
            case 'weekly':
                format = { weekday: 'short' }
                break
            case 'monthly':
                format = { day: 'numeric' }
                break
            case 'yearly':
                format = { month: 'short' }
                break
        }

        return format
    },

    formatDateForReportType(date: Date, reportType: SalesReportSettingsType) {
        return new Intl.DateTimeFormat(navigator.language, this.getFormatForReportType(reportType)).format(date)
    },

    getCenterDatetime(gmt: number) {
        const date = DateTime.utc()
        let centerDate = date.setZone(this.getUtcString(gmt))
        if (centerDate.hour < 4 && centerDate.hour >= 0) {
            centerDate = centerDate.minus({ days: 1 })
        }

        return centerDate
    },

    getRealCenterDatetime(gmt: number) {
        return DateTime.utc().setZone(this.getUtcString(gmt))
    },

    getUtcString(gmt: number): string {
        if (gmt >= 0) {
            return `UTC+${gmt}`
        } else if (gmt < 0) {
            return `UTC${gmt}`
        } else {
            return `UTC${gmt}`
        }
    },

    /**
     * Check if user has centers with different timezones
     */
    async hasMultipleTimezones(): Promise<boolean> {
        try {
            const storedCentersJson = await Preferences.get({ key: 'centers' })
            if (!storedCentersJson.value) return false

            const storedCenters = JSON.parse(storedCentersJson.value)
            const timezones = new Set<number>()

            storedCenters.forEach((center: any) => {
                if (center.gmt !== undefined) {
                    timezones.add(center.gmt)
                }
            })

            return timezones.size > 1
        } catch (error) {
            console.error('Error checking multiple timezones:', error)
            return false
        }
    },

    /**
     * Check if a report type is daily and user has multiple timezones
     */
    async shouldShowLocalTime(reportType: SalesReportSettingsType): Promise<boolean> {
        return reportType === 'daily' && await this.hasMultipleTimezones()
    },

    /**
     * Get the local time for a specific center
     */
    async getCenterLocalTime(centerId: number): Promise<string | null> {
        try {
            const storedCentersJson = await Preferences.get({ key: 'centers' })
            if (!storedCentersJson.value) return null

            const storedCenters = JSON.parse(storedCentersJson.value)
            const storedCenter = storedCenters.find((c: any) => c.id == centerId)

            if (storedCenter?.gmt !== undefined) {
                const gmtOffset = storedCenter.gmt
                const utcString = gmtOffset >= 0 ? `UTC+${gmtOffset}` : `UTC${gmtOffset}`
                const localDateTime = DateTime.utc().setZone(utcString)
                return localDateTime.toFormat('HH:mm')
            }

            return null
        } catch (error) {
            console.error('Error getting center local time:', error)
            return null
        }
    }
}